import React, { useState, useEffect } from 'react';

function QuestionPhase({ phase, questions, onComplete, initialAnswers }) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phaseAnswers, setPhaseAnswers] = useState(() => {
    // Load existing answers for this phase
    const existing = {};
    questions.questions.forEach(q => {
      if (initialAnswers[q.id]) {
        existing[q.id] = initialAnswers[q.id];
      }
    });
    return existing;
  });

  // CRITICAL FIX: Reset question index when phase changes
  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [phase]);

  const currentQuestion = questions.questions[currentQuestionIndex];
  const totalQuestions = questions.questions.length;
  const phaseNumber = phase === 'phase1' ? 1 : phase === 'phase2' ? 2 : 3;

  const handleAnswer = (answer) => {
    const newAnswers = {
      ...phaseAnswers,
      [currentQuestion.id]: answer
    };
    setPhaseAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // Phase complete
      onComplete(phaseAnswers);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const isAnswered = () => {
    const answer = phaseAnswers[currentQuestion.id];
    if (currentQuestion.type === 'text') {
      return answer && answer.trim().length > 0;
    } else if (currentQuestion.type === 'multiple') {
      return answer && answer.length > 0;
    } else {
      return answer !== undefined && answer !== null && answer !== '';
    }
  };

  const renderQuestion = () => {
    const currentAnswer = phaseAnswers[currentQuestion.id];

    switch (currentQuestion.type) {
      case 'single':
        return (
          <div className="options-container">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                className={`option-button ${currentAnswer === option ? 'selected' : ''}`}
                onClick={() => handleAnswer(option)}
              >
                <span className="option-radio">
                  {currentAnswer === option ? '●' : '○'}
                </span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>
        );

      case 'multiple':
        const selectedOptions = currentAnswer || [];
        return (
          <div className="options-container">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedOptions.includes(option);
              return (
                <button
                  key={index}
                  className={`option-button ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    if (isSelected) {
                      handleAnswer(selectedOptions.filter(o => o !== option));
                    } else {
                      handleAnswer([...selectedOptions, option]);
                    }
                  }}
                >
                  <span className="option-checkbox">
                    {isSelected ? '☑' : '☐'}
                  </span>
                  <span className="option-text">{option}</span>
                </button>
              );
            })}
          </div>
        );

      case 'text':
        return (
          <div className="text-input-container">
            <textarea
              value={currentAnswer || ''}
              onChange={(e) => handleAnswer(e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows="5"
              className="text-input"
            />
          </div>
        );

      case 'ranking':
        // For Q11 - ranking top 3 roles
        // Simplified version - would need enhancement for full drag-and-drop
        const roles = phaseAnswers[9] || initialAnswers[9] || []; // Get roles from Q9
        const rankings = currentAnswer || {};
        
        return (
          <div className="ranking-container">
            <p className="ranking-instruction">{currentQuestion.instruction}</p>
            {[1, 2, 3].map(rank => (
              <div key={rank} className="ranking-row">
                <label className="ranking-label">Priority #{rank}:</label>
                <select
                  value={rankings[rank] || ''}
                  onChange={(e) => {
                    handleAnswer({
                      ...rankings,
                      [rank]: e.target.value
                    });
                  }}
                  className="ranking-select"
                >
                  <option value="">-- Select --</option>
                  {roles.filter(role => 
                    // Only show roles not already selected in other ranks
                    !Object.values(rankings).includes(role) || rankings[rank] === role
                  ).map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="question-phase">
      <div className="container">
        {/* Progress bar */}
        <div className="progress-container">
          <div className="progress-header">
            <span className="progress-label">Phase {phaseNumber} of 3</span>
            <span className="progress-count">
              Question {currentQuestionIndex + 1} of {totalQuestions}
            </span>
          </div>
          <div className="progress-bar-bg">
            <div 
              className="progress-bar-fill"
              style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Phase title */}
        <div className="phase-header">
          <h2 className="phase-title">{questions.title}</h2>
          <p className="phase-subtitle">{questions.subtitle}</p>
        </div>

        {/* Current question */}
        <div className="question-container">
          <h3 className="question-text">
            <span className="question-number">{currentQuestionIndex + 1}.</span>
            {currentQuestion.question}
          </h3>

          {renderQuestion()}
        </div>

        {/* Navigation buttons */}
        <div className="navigation-buttons">
          {currentQuestionIndex > 0 && (
            <button 
              className="nav-button back-button"
              onClick={handleBack}
            >
              ← Back
            </button>
          )}
          
          <button 
            className="nav-button next-button"
            onClick={handleNext}
            disabled={!isAnswered()}
          >
            {currentQuestionIndex === totalQuestions - 1 
              ? (phaseNumber === 3 ? 'See My Results →' : 'Continue to Next Phase →')
              : 'Next Question →'
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuestionPhase;
